import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery, useTheme } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';

import PresetThumb from './PresetThumb';
import { defaultPresetId, layoutFromPreset, presetIdsFor } from './presets';

/** Design screen 8: pick a preset (replaces the current layout), or restore Klassinen. */
export default function PresetPickerDialog({ open, onClose, organization, currentPresetId, currentCount, onApply, onRestoreClassic }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [selected, setSelected] = useState(null);
  const ids = presetIdsFor(organization);
  const currentName = currentPresetId ? t(`dashboard.presets.${currentPresetId}.name`) : t('dashboard.presets.custom');

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '16px' } }} data-testid="preset-picker">
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>{t('dashboard.edit.presets')}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" data-testid="preset-warning" sx={{ mb: 2 }}>
          {t('dashboard.presets.replaceWarning', { name: currentName, count: currentCount })}
        </Alert>
        <Box sx={{ display: 'grid', gridTemplateColumns: fullScreen ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5 }}>
          {ids.map((id) => {
            const layout = layoutFromPreset(id, organization);
            const isSelected = selected === id;
            const inUse = currentPresetId === id;
            return (
              <Box
                key={id}
                data-testid={`preset-card-${id}`}
                onClick={() => setSelected(id)}
                sx={{ border: `2px solid ${isSelected ? '#3182ce' : '#e2e8f0'}`, borderRadius: '12px', p: 1.5, cursor: 'pointer', bgcolor: isSelected ? '#f5f9ff' : '#fff', display: 'flex', flexDirection: 'column', gap: 1 }}
              >
                <PresetThumb layout={layout} width={fullScreen ? 260 : 200} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>{t(`dashboard.presets.${id}.name`)}</Typography>
                  {inUse ? <Chip label={t('dashboard.presets.inUse')} size="small" sx={{ height: 20, fontSize: 11, bgcolor: 'rgba(49,130,206,0.10)', color: '#3182ce' }} /> : null}
                </Box>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t(`dashboard.presets.${id}.desc`)}</Typography>
                <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{t('dashboard.presets.widgetCount', { count: layout.widgets.length })}</Typography>
              </Box>
            );
          })}
        </Box>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 2 }}>{t('dashboard.presets.gateNote')}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button variant="text" startIcon={<HistoryIcon />} data-testid="preset-restore-classic" onClick={() => onRestoreClassic(defaultPresetId(organization))} sx={{ mr: 'auto', color: 'secondary.main' }}>
          {t('dashboard.presets.restoreClassic')}
        </Button>
        <Button variant="outlined" onClick={onClose}>
          {t('dashboard.edit.cancel')}
        </Button>
        <Button variant="contained" color="secondary" disabled={!selected} data-testid="preset-apply" onClick={() => onApply(selected)}>
          {selected ? t('dashboard.presets.useTemplate', { name: t(`dashboard.presets.${selected}.name`) }) : t('dashboard.presets.useTemplateEmpty')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
